// =============================================================================
// domain/accountCard.ts — everything one Accounts card says, as data.
// -----------------------------------------------------------------------------
// `domain/account.ts` holds the rule ENGINE — balance, floor, buffer, target,
// consistency, position size. This file is the layer above it: the state an
// account is IN, the urgency it sorts by, the four gauges, and the house-wide
// roll-up across every card.
//
// It produces DATA, never markup, and the four-slot gauge row is the reason
// that matters. The old client builds those slots inside its HTML renderer, so
// "there are always exactly four, in this order, and an unset rule reserves its
// slot" is a property of a template string that nothing can check. Here it is a
// returned array, and a test pins the count and the order — which is the whole
// point of the standardisation (Josh's call, 2026-06-10): cards of different
// stages must scan without the eye fighting layout shift.
//
// ── 🚨 THREE INDEPENDENT STATES, NEVER ONE STRING ──
//
//   lifecycle   what the TRADER says: active · paused · passed · blown · closed
//   compliance  what the RULES say: healthy · caution · danger · failed, or
//               nothing at all when the account has no drawdown rule
//   locked      whether today's loss limit has already been hit
//
// The old client collapsed these into one `accountDisplayStatus` and produced
// contradictions on a single card — a "warn" pill above a gauge reading FAILED —
// because two state machines were sharing one render slot. They are separate
// here for that reason, and `accountCardSignal` composes them in ONE place so
// the stripe, the sort order and the pill cannot disagree.
//
// ── ⚠ EVERY NUMBER IN HERE IS CLASS L (D47) ──
//
// Balance, peak, floor, buffer, target progress, consistency and days-traded all
// read the whole trade history. Computed over a bounded read they are not less
// precise — they are WRONG IN THE OPTIMISTIC DIRECTION and self-consistent while
// wrong. This module stays honest for whatever array it is handed, exactly like
// the rest of `domain/`; `selectors/accountCards.ts` is what refuses a partial
// population, and the UI reaches this file only through it.
//
// ── ⚠ NO WALL CLOCK (D26) ──
// `today` is a parameter at every entry point. A function whose answer changes
// tomorrow for the same input cannot be parity-checked against the old client.
// =============================================================================

import {
  accountDisplayBalance,
  computeConsistencyMath,
  computeDaysProgress,
  computeDrawdownBuffer,
  computeDrawdownBufferState,
  computeDrawdownFloor,
  computePositionSizeUsage,
  computeProfitTargetProgress,
  tradesInAttempt,
  type AccountLike,
  type AccountTrade,
  type ConsistencyMath,
  type DrawdownBufferState,
} from './account';
import {
  dailyPnl, riskOfRuin, seededRandom, type RuinResult, type RuinRules,
} from './riskOfRuin';
import { effectiveTradeDate } from './session';
import type { DayBoundaryMode } from './types';

// ---------------------------------------------------------------------------
// The account shape a card needs
// ---------------------------------------------------------------------------

/**
 * ⚠ EXTENDS `AccountLike` STRUCTURALLY rather than importing the concrete
 * `Account`, for the same reason the engine below it does: a test fixture is
 * eight fields instead of twenty-six, and the module stays callable from the
 * Deno aggregate, which has no UI account model.
 */
export interface CardAccount extends AccountLike {
  name: string;
  firm: string;
  accountSize: number;
  externalId: string;
  status: string;
  excludeFromMetrics: boolean;
  archived: boolean;
  feeWaived: boolean;
  feeCadence: string;
  feeAmount: number;
  /**
   * ⚠ READ BY THE CARD, NOT BY `accountSetupMissing`. The list of missing fields
   * stays true whether or not the trader has waved the prompt away — other
   * readers ask what is missing, and a dismissal is an answer about the PROMPT.
   * The old client gates the same way, at the render.
   */
  setupNudgeDismissed?: boolean;
}

// ---------------------------------------------------------------------------
// The three states
// ---------------------------------------------------------------------------

export type Lifecycle = 'active' | 'paused' | 'passed' | 'blown' | 'closed';

const LIFECYCLES: readonly string[] = ['active', 'paused', 'passed', 'blown', 'closed'];

/** What the trader says about this account. Anything unrecognised is active. */
export function accountLifecycle(a: Pick<CardAccount, 'status'>): Lifecycle {
  const s = String(a?.status ?? 'active').toLowerCase();
  return (LIFECYCLES.includes(s) ? s : 'active') as Lifecycle;
}

export function lifecycleLabel(l: Lifecycle): string {
  return l.charAt(0).toUpperCase() + l.slice(1);
}

/** Terminal accounts are history: they have no live gauge left to read. */
export function isTerminal(l: Lifecycle): boolean {
  return l === 'passed' || l === 'blown' || l === 'closed';
}

/**
 * This account's net P&L today.
 *
 * ⚠ `effectiveTradeDate`, never the raw column. In session mode an evening trade
 * belongs to the next session, and reading `t.date` here would put it on the
 * wrong side of the daily loss limit — which is the one number on this card that
 * stops a trader mid-session.
 */
export function accountTodayPnl(
  a: Pick<CardAccount, 'id'>,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): number {
  let sum = 0;
  for (const t of trades) {
    if (!t || t.accountId !== a.id) continue;
    if (effectiveTradeDate(t, mode) !== today) continue;
    sum += Number(t.netPnl) || 0;
  }
  return sum;
}

/**
 * Has today's loss limit already been hit?
 *
 * ⚠ GUARDED AT THE SOURCE ON LIFECYCLE. A stray trade dated today on a blown or
 * closed account — a CSV slip, a hand edit — would otherwise raise a "no new
 * trades until 06:00 ET" banner on an account nobody is trading. Every surface
 * that reads `locked` gets the same answer because the guard is here rather than
 * in each renderer.
 *
 * The limit is stored positive and losses are negative, hence the comparison
 * against its negation.
 */
export function accountIsLocked(
  a: CardAccount,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): boolean {
  if (accountLifecycle(a) !== 'active') return false;
  const limit = Number(a.rules?.dailyLossLimit) || 0;
  if (limit <= 0) return false;
  return accountTodayPnl(a, trades, today, mode) <= -Math.abs(limit);
}

/**
 * What the drawdown rule says — or NOTHING when there is no drawdown rule.
 *
 * ⚠ The null matters. `computeDrawdownBufferState` answers "healthy, 100%" for
 * an account with no rule so its callers get a non-alarming number, and a card
 * that showed that as a green compliance state would be claiming an account
 * satisfies a limit its firm never set.
 */
export function accountCompliance(
  a: CardAccount,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): DrawdownBufferState | null {
  if (!(Number(a.rules?.maxDDValue) > 0)) return null;
  return computeDrawdownBufferState(a, trades, today, mode);
}

export type CardSignal =
  | 'locked' | 'blown' | 'failed' | 'danger' | 'caution'
  | 'paused' | 'passed' | 'closed' | 'ok';

/**
 * The one dominant signal, composed from the three states above.
 *
 * ⚠ LIFECYCLE BEATS COMPLIANCE, and that ordering is the point: a blown or
 * closed account is final, and painting it "danger" would read as an account
 * still trading toward a breach it has already had.
 */
export function accountCardSignal(
  lifecycle: Lifecycle,
  compliance: DrawdownBufferState | null,
  locked: boolean,
): CardSignal {
  if (locked) return 'locked';
  if (lifecycle === 'blown') return 'blown';
  if (lifecycle === 'closed') return 'closed';
  if (lifecycle === 'passed') return 'passed';
  if (lifecycle === 'paused') return 'paused';
  if (compliance?.state === 'failed') return 'failed';
  if (compliance?.state === 'danger') return 'danger';
  if (compliance?.state === 'caution') return 'caution';
  return 'ok';
}

/** Higher sorts first under "At Risk First". */
export function accountSignalWeight(s: CardSignal): number {
  switch (s) {
    case 'locked': return 100;
    case 'blown': return 90;
    case 'failed': return 80;
    case 'danger': return 70;
    case 'caution': return 60;
    case 'paused': return 30;
    case 'passed': return 20;
    case 'closed': return 10;
    default: return 0;
  }
}

/** A remaining-room bar: green above 60%, amber to 30%, red below. */
export function barTone(pctRemaining: number): '' | 'amber' | 'red' {
  if (pctRemaining >= 60) return '';
  if (pctRemaining >= 30) return 'amber';
  return 'red';
}

// ---------------------------------------------------------------------------
// Setup completeness
// ---------------------------------------------------------------------------

/**
 * Fields this account is missing, in the words the nudge prints.
 *
 * ⚠ A PERSONAL account is exempt entirely. It has no firm, no evaluation fee and
 * no prop rules, so asking for them produced a permanent banner in the old
 * client whose only escape was dismissal.
 *
 * ⚠ A WAIVED FEE IS A COMPLETE ANSWER, not a missing one. `feeWaived` is a
 * separate fact from `feeAmount === 0`: collapsing them lets an account whose
 * cost was never entered masquerade as a free one and flatters cost-per-funded.
 *
 * ⚠ A SIM / REPLAY account is never asked for a COST. It did not cost real
 * money, and a figure typed there would flow straight into the economics.
 *
 * ⚠ THE OLD CLIENT ALSO ASKS A live-funded ACCOUNT WHAT IT WAS ACTIVATED FROM.
 * That is NOT ported, because this client does not model the field — there is no
 * column, no loader read and nothing to write it back with, so listing it would
 * name a field the trader cannot supply. The consequence is stated rather than
 * hidden: on a live-funded account this list can be one shorter than the old
 * client's.
 */
/**
 * The account types that cost money to hold — an evaluation fee, a reset, a
 * monthly. The old client's `isPropCostBearing`, verbatim.
 *
 * ⚠ `funded` (the legacy type) AND `custom` are deliberately absent. Neither
 * says the account was bought from a firm, and asking for a cost on one puts an
 * item on the missing-fields list that can never honestly be cleared.
 */
const COST_BEARING_TYPES: ReadonlySet<string> = new Set([
  'combine', 'sim-funded', 'live-funded',
]);

export function accountSetupMissing(a: CardAccount): string[] {
  if (!a) return [];
  if (a.accountType === 'personal') return [];
  // 🚨 THE TYPE DECIDES WHETHER A COST EXISTS, not just the metrics flag (#73).
  // The old client's `isPropCostBearing` (app/index.html:13220) asks for a cost
  // on a combine and on the two funded types only — plain "Funded (legacy)" and
  // "Custom" are excluded, because neither describes an account bought from a
  // firm. Reading the metrics flag alone asked every non-SIM account for a fee
  // it may never have had, and put a field on the missing list that the trader
  // could only clear by inventing a number.
  const costBearing = !a.excludeFromMetrics && COST_BEARING_TYPES.has(
    String(a.accountType ?? ''),
  );
  const miss: string[] = [];
  if (!a.name) miss.push('name');
  if (!a.firm) miss.push('firm');
  if (!a.externalId) miss.push('account ID');
  if (!(Number(a.accountSize) > 0)) miss.push('account size');
  if (costBearing && !(Number(a.feeAmount) > 0) && a.feeWaived !== true) miss.push('cost');
  // A configuration warning rather than a field, and it belongs here: without a
  // daily loss limit the card's Today's Loss Room bar has nothing to measure.
  if (!(Number(a.rules?.dailyLossLimit) > 0)) miss.push('daily loss limit');
  return miss;
}

// ---------------------------------------------------------------------------
// The four gauges
// ---------------------------------------------------------------------------

export type GaugeState =
  | 'healthy' | 'caution' | 'danger' | 'failed' | 'achieved' | 'not-started';

export interface GaugeSlot {
  /** The slot's identity, so a test names it rather than counting positions. */
  key: 'drawdown' | 'profit' | 'days' | 'size';
  label: string;
  state: GaugeState;
  /** 0–100. Ignored when `unset`. */
  pct: number;
  /** The figure inside the arc. */
  centre: string;
  sub: string;
  meta: string;
  /** No rule configured: the tile renders muted and `hint` says what to set. */
  unset: boolean;
  hint: string;
}

const empty = (
  key: GaugeSlot['key'], label: string, hint: string,
): GaugeSlot => ({
  key, label, state: 'not-started', pct: 0, centre: '—',
  sub: 'Not configured', meta: '', unset: true, hint,
});

/** How money reads inside a gauge — whole dollars, always signed by context. */
const usd0 = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** The ring centre is small; "+$1,920" overflows it. The exact figure is in
 *  `sub` directly beneath, so this can abbreviate without losing anything. */
function compactSigned(v: number): string {
  const sign = v < 0 ? '−' : '+';
  const n = Math.abs(v);
  if (n >= 10_000) return `${sign}$${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${sign}$${(n / 1000).toFixed(1)}k`;
  return `${sign}$${n.toFixed(0)}`;
}

export const FUNDED_TYPES: ReadonlySet<string> = new Set(['funded', 'sim-funded', 'live-funded']);

/**
 * ALWAYS FOUR SLOTS, ALWAYS IN THIS ORDER.
 *
 *   1 DRAWDOWN BUFFER   every prop account has one
 *   2 PROFIT TARGET     or NET P&L on a funded account, which has no target
 *   3 TRADING DAYS      an evaluation metric; funded accounts have no minimum
 *   4 POSITION SIZE     any account with a contract cap
 *
 * ⚠ An unset rule RESERVES its slot rather than dropping out. Cards at different
 * stages otherwise render two, three and four gauges and stop lining up with
 * each other, which is exactly the layout shift this row exists to prevent.
 */
export function complianceGauges(
  a: CardAccount,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): GaugeSlot[] {
  const r = a.rules ?? {};
  const funded = a.accountType === 'live-funded';

  // ── 1. Drawdown buffer ────────────────────────────────────────────────────
  let drawdown: GaugeSlot;
  if (Number(r.maxDDValue) > 0) {
    const st = computeDrawdownBufferState(a, trades, today, mode);
    const buffer = computeDrawdownBuffer(a, trades, today, mode);
    const floor = computeDrawdownFloor(a, trades, today, mode);
    const ddType = r.maxDDType || 'static';
    const lockMech = r.lockMechanic || 'trail-forever';
    const typeLabel = ddType === 'trailing-intraday' ? 'intraday trail'
      : ddType === 'trailing-eod'
        ? (lockMech.startsWith('trail-then-freeze') ? 'EOD trail · locks' : 'EOD trail')
        : 'static';
    drawdown = {
      key: 'drawdown',
      label: 'DRAWDOWN BUFFER',
      state: st.state,
      pct: st.pct,
      centre: st.state === 'failed' ? 'FAILED' : `${st.pct.toFixed(0)}%`,
      sub: st.state === 'failed'
        ? `${usd0(Math.abs(buffer))} under floor`
        : `${usd0(buffer)} buffer`,
      meta: `Floor: ${usd0(floor)} · ${typeLabel}`,
      unset: false,
      hint: '',
    };
  } else {
    drawdown = empty('drawdown', 'DRAWDOWN BUFFER', 'Set max drawdown in rules');
  }

  // ── 2. Profit target, or net P&L on a funded account ──────────────────────
  let profit: GaugeSlot;
  if (Number(r.profitTarget) > 0) {
    const pt = computeProfitTargetProgress(a, trades, today, mode);
    profit = {
      key: 'profit',
      label: 'PROFIT TARGET',
      state: pt.state === 'achieved' ? 'achieved'
        : pt.pct > 50 ? 'healthy' : pt.pct > 0 ? 'caution' : 'not-started',
      pct: pt.pct,
      centre: pt.state === 'achieved' ? 'HIT' : `${pt.pct.toFixed(0)}%`,
      sub: pt.state === 'achieved'
        ? `${usd0(pt.profitSoFar ?? 0)} earned`
        : `${usd0(pt.dollarsToGo)} to go`,
      meta: `Target: ${usd0(pt.target ?? 0)}`,
      unset: false,
      hint: '',
    };
  } else if (funded) {
    // A funded account has no target, which left this slot empty and the card
    // with no profit signal anywhere on it. The ring is EARNED CUSHION: profit
    // measured against the account's own max drawdown. At 100% you have banked a
    // full drawdown's worth of room above where you started — a real quantity,
    // rather than progress toward a target that does not exist.
    const net = accountDisplayBalance(a, trades, today, mode) - (a.startingBalance ?? a.accountSize ?? 0);
    const ddBase = Number(r.maxDDValue) || 0;
    const cushionPct = ddBase > 0 ? Math.max(0, Math.min(100, (net / ddBase) * 100)) : 0;
    profit = {
      key: 'profit',
      label: 'NET P&L',
      state: ddBase <= 0 ? 'not-started' : net >= 0 ? 'healthy' : 'danger',
      pct: cushionPct,
      centre: compactSigned(net),
      sub: `${usd0(net)} since start`,
      meta: ddBase > 0
        ? `${cushionPct.toFixed(0)}% of ${usd0(ddBase)} drawdown earned`
        : 'Set max drawdown to gauge cushion',
      unset: false,
      hint: '',
    };
  } else {
    profit = empty('profit', 'PROFIT TARGET', 'Set in account rules');
  }

  // ── 3. Trading days ───────────────────────────────────────────────────────
  let days: GaugeSlot;
  const dp = computeDaysProgress(a, trades, today, mode);
  if (dp && dp.minDays > 0) {
    days = {
      key: 'days',
      label: 'TRADING DAYS',
      state: dp.minMet ? 'achieved' : dp.minProgress > 50 ? 'caution' : 'not-started',
      pct: dp.minProgress,
      centre: dp.minMet ? 'MET' : `${dp.tradingDays}/${dp.minDays}`,
      sub: dp.minMet
        ? `${dp.tradingDays} days traded`
        : `${dp.minDays - dp.tradingDays} more needed`,
      meta: '',
      unset: false,
      hint: '',
    };
  } else if (funded) {
    days = empty('days', 'TRADING DAYS', 'Funded — no minimum');
  } else {
    days = empty('days', 'TRADING DAYS', 'Set min trading days');
  }

  // ── 4. Position size ──────────────────────────────────────────────────────
  let size: GaugeSlot;
  const pos = computePositionSizeUsage(a, trades, today, mode);
  if (pos) {
    size = {
      key: 'size',
      label: 'POSITION SIZE',
      state: pos.todayOver ? 'failed'
        : pos.todayPct > 75 ? 'danger' : pos.todayPct > 50 ? 'caution' : 'healthy',
      pct: pos.todayPct,
      centre: pos.todayOver ? 'OVER' : `${pos.todayPct.toFixed(0)}%`,
      sub: `${pos.todayMax} / ${pos.capContracts} today`,
      meta: pos.allTimeMax > 0 ? `All-time max: ${pos.allTimeMax}` : '',
      unset: false,
      hint: '',
    };
  } else {
    size = empty('size', 'POSITION SIZE', 'Set max contracts');
  }

  return [drawdown, profit, days, size];
}

// ---------------------------------------------------------------------------
// The card model
// ---------------------------------------------------------------------------

export interface AccountCardModel {
  a: CardAccount;
  lifecycle: Lifecycle;
  compliance: DrawdownBufferState | null;
  locked: boolean;
  signal: CardSignal;
  terminal: boolean;
  /** Balance minus the account's own starting point. */
  pnlVsStart: number;
  /** The drawdown cushion in dollars, floored at 0. Zero when no rule is set. */
  ddRoom: number;
  /** That cushion as a share of the drawdown, 0–100. */
  ddPct: number;
  dailyLimit: number;
  todayPnl: number;
  /** Dollars of loss still allowed today, or null when no limit is set. */
  todayRoom: number | null;
  /** That room as a share of the limit, 0–100. */
  todayPct: number;
  consistency: ConsistencyMath | null;
  gauges: GaugeSlot[];
  /** Percent of resampled futures that hit the floor, or null. See below. */
  ruin: RuinResult | null;
  /** Why ruin could not be modelled, in the trader's words. Null when it could. */
  ruinBlocked: string | null;
  /**
   * 🚨 THE SIMULATOR'S INPUTS, CARRIED SO THE TWO CANNOT DISAGREE.
   *
   * The card prints one number and the simulator behind it prints a fan of
   * them. If each derived its own pool, its own starting balance and its own
   * lock, they would drift apart the first time one of those derivations
   * changed — and the old client has already shipped exactly that: its card
   * resamples from the CURRENT balance while its simulator starts from the
   * account's ORIGINAL one, so the same account reads two different risks
   * depending on which you opened. Same inputs, one place.
   */
  ruinDays: number[];
  ruinStart: number;
  ruinRules: RuinRules;
  missing: string[];
}

/**
 * ── 🚨 RUIN REFUSES RATHER THAN INVENTING A LIMIT — a deliberate divergence ──
 *
 * The old client's card version substitutes `maxDDValue || 2500` and
 * `profitTarget || 5000`, so an account with NO drawdown rule gets a confident
 * percentage resampled against a $2,500 floor its firm never set, and a funded
 * account gets a $5,000 target that ENDS simulated runs early and therefore
 * UNDERSTATES ruin. Both are fabricated inputs presented as a risk figure.
 *
 * Here `riskOfRuin` returns null without a real drawdown, and `null` genuinely
 * means "funded, nothing ends the run early". The card says which input is
 * missing instead of printing a number.
 *
 * ⚠ SEEDED on the account and the day, so the same book always shows the same
 * figure. A risk number that moves between renders is not a risk number.
 */
export function buildAccountCardModel(
  a: CardAccount,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
): AccountCardModel {
  const r = a.rules ?? {};
  const lifecycle = accountLifecycle(a);
  const compliance = accountCompliance(a, trades, today, mode);
  const locked = accountIsLocked(a, trades, today, mode);

  const dd = Number(r.maxDDValue) || 0;
  const ddRoom = dd > 0 ? Math.max(0, computeDrawdownBuffer(a, trades, today, mode)) : 0;
  const ddPct = dd > 0 ? Math.max(0, Math.min(100, (ddRoom / dd) * 100)) : 0;

  const dailyLimit = Number(r.dailyLossLimit) || 0;
  const todayPnl = accountTodayPnl(a, trades, today, mode);
  const todayLoss = Math.max(0, -todayPnl);
  const todayRoom = dailyLimit > 0 ? Math.max(0, dailyLimit - todayLoss) : null;
  const todayPct = dailyLimit > 0
    ? Math.max(0, Math.min(100, ((todayRoom ?? 0) / dailyLimit) * 100)) : 0;

  // ⚠ `??` not `||` — a starting balance of 0 is legitimate (a Topstep XFA
  // starts there) and must not fall through to the account size.
  const pnlVsStart = accountDisplayBalance(a, trades, today, mode)
    - (a.startingBalance ?? a.accountSize ?? 0);

  const attempt = tradesInAttempt(a, trades, today, mode);
  const ruinDays = dailyPnl(attempt);
  // ⚠ The DISPLAY balance, never the raw `currentBalance` column. That field is
  // only re-synced by the account editor — the reset path never touches it — so
  // after a reset it is the BLOWN balance, and seeding the simulation from a
  // dead attempt is exactly the bug its docstring names.
  const ruinStart = accountDisplayBalance(a, trades, today, mode);
  const ruinRules: RuinRules = {
    maxDrawdown: dd,
    profitTarget: Number(r.profitTarget) > 0 ? Number(r.profitTarget) : null,
    lockMechanic: r.lockMechanic ?? null,
    lockPad: Number(r.lockPad) || 0,
  };
  const ruin = dd > 0
    ? riskOfRuin({
      days: ruinDays,
      start: ruinStart,
      rules: ruinRules,
      rand: seededRandom(`${a.id}|${today}`),
    })
    : null;

  const ruinBlocked = dd > 0
    ? (ruin ? null : 'not enough trading days yet')
    : 'no max drawdown set on this account';

  return {
    a,
    lifecycle,
    compliance,
    locked,
    signal: accountCardSignal(lifecycle, compliance, locked),
    terminal: isTerminal(lifecycle),
    pnlVsStart,
    ddRoom,
    ddPct,
    dailyLimit,
    todayPnl,
    todayRoom,
    todayPct,
    consistency: computeConsistencyMath(a, trades, today, mode),
    gauges: complianceGauges(a, trades, today, mode),
    ruin,
    ruinBlocked,
    ruinDays,
    ruinStart,
    ruinRules,
    missing: accountSetupMissing(a),
  };
}

// ---------------------------------------------------------------------------
// Filtering and sorting
// ---------------------------------------------------------------------------

export type CardFilter = 'all' | 'active' | 'eval' | 'funded' | 'locked';
export type CardSort = 'risk' | 'pnl' | 'status' | 'firm';

export function cardMatchesFilter(m: AccountCardModel, f: CardFilter): boolean {
  if (f === 'all') return true;
  if (f === 'locked') return m.locked;
  if (f === 'eval') return m.a.accountType === 'combine';
  if (f === 'funded') return FUNDED_TYPES.has(m.a.accountType);
  return m.lifecycle === 'active';
}

export function cardFilterCounts(
  models: readonly AccountCardModel[],
): Record<CardFilter, number> {
  return {
    all: models.length,
    active: models.filter((m) => m.lifecycle === 'active').length,
    eval: models.filter((m) => m.a.accountType === 'combine').length,
    funded: models.filter((m) => FUNDED_TYPES.has(m.a.accountType)).length,
    locked: models.filter((m) => m.locked).length,
  };
}

/**
 * ⚠ EXCLUDED ACCOUNTS SINK TO THE BOTTOM WHATEVER THE SORT. A SIM or Replay
 * account is a big card and never the thing a trader opened this tab to check.
 */
export function sortAccountCards(
  models: readonly AccountCardModel[],
  sort: CardSort,
): AccountCardModel[] {
  return models.slice().sort((x, y) => {
    const xe = x.a.excludeFromMetrics ? 1 : 0;
    const ye = y.a.excludeFromMetrics ? 1 : 0;
    if (xe !== ye) return xe - ye;
    if (sort === 'risk') {
      const xw = accountSignalWeight(x.signal);
      const yw = accountSignalWeight(y.signal);
      if (xw !== yw) return yw - xw;
      // ⚠ A missing ruin figure sorts as LESS urgent than any real one. Treating
      // "not enough days" as 0% would be a claim; −1 keeps it out of the ranking.
      const xr = x.ruin?.blowPct ?? -1;
      const yr = y.ruin?.blowPct ?? -1;
      if (xr !== yr) return yr - xr;
      return x.ddPct - y.ddPct;
    }
    if (sort === 'pnl') return y.pnlVsStart - x.pnlVsStart;
    if (sort === 'status') return x.lifecycle.localeCompare(y.lifecycle);
    return (x.a.firm || '').localeCompare(y.a.firm || '');
  });
}

// ---------------------------------------------------------------------------
// The house-wide roll-up
// ---------------------------------------------------------------------------

export type HouseHealth = 'ok' | 'warn' | 'danger';

export interface HouseAggregate {
  fundedBalance: number;
  todayPnl: number;
  weekPnl: number;
  /** Room before bust, summed over accounts STILL AT RISK. See below. */
  ddBuffer: number;
  worstRuin: number | null;
  worstRuinAccount: string | null;
  health: HouseHealth;
  healthLabel: string;
  fundedCount: number;
}

/** A card is "warning" when its rules are unhappy or ruin is above a quarter. */
function isWarning(m: AccountCardModel): boolean {
  const cs = m.compliance?.state;
  return cs === 'caution' || cs === 'danger' || cs === 'failed'
    || (m.ruin != null && m.ruin.blowPct > 25);
}

/**
 * House health across the cards on screen.
 *
 * ── ⚠ THE RISK CELLS COUNT ONLY ACCOUNTS STILL AT RISK ──
 *
 * DD Buffer and Worst RoR include `active` accounts only. A passed combine, a
 * blown account and a closed one are HISTORY — their buffer is frozen and there
 * is nothing left to lose on them, so rolling them in either inflates the room
 * that exists or understates the risk. Today's and this week's P&L still count
 * them, because a closing trade on a blown day is realised money.
 *
 * ── 🚨 A DELIBERATE DIVERGENCE: SIM MONEY IS NOT IN EITHER P&L CELL ──
 *
 * The old client's banner contradicts itself — Today's P&L sums every card
 * INCLUDING SIM and Replay accounts, while Week P&L filters them out. Two cells,
 * one row, two populations, and a SIM day moves one and not the other. Both
 * exclude them here. This is a house-health banner: it is about real money, and
 * an account the trader has flagged as not counting must not count.
 */
export function buildHouseAggregate(
  models: readonly AccountCardModel[],
  trades: readonly AccountTrade[],
  today: string,
  weekStart: string,
  mode: DayBoundaryMode,
): HouseAggregate {
  const counting = models.filter((m) => !m.a.excludeFromMetrics);
  const atRisk = models.filter((m) => m.lifecycle === 'active');
  const fundedModels = models.filter((m) => FUNDED_TYPES.has(m.a.accountType));

  const fundedBalance = fundedModels.reduce(
    (s, m) => s + accountDisplayBalance(m.a, trades, today, mode), 0);

  const todayPnl = counting.reduce((s, m) => s + m.todayPnl, 0);

  // Week P&L is scoped to the accounts THIS BANNER SUMMARISES, so the archived
  // group split out below the fold cannot leak into one cell and not the other.
  const ids = new Set(counting.map((m) => m.a.id));
  let weekPnl = 0;
  for (const t of trades) {
    if (!t || t.accountId == null || !ids.has(t.accountId)) continue;
    const d = effectiveTradeDate(t, mode);
    if (!d || d < weekStart) continue;
    weekPnl += Number(t.netPnl) || 0;
  }

  const ddBuffer = atRisk.reduce((s, m) => s + m.ddRoom, 0);

  let worstRuin: number | null = null;
  let worstRuinAccount: string | null = null;
  for (const m of atRisk) {
    if (!m.ruin) continue;
    if (worstRuin == null || m.ruin.blowPct > worstRuin) {
      worstRuin = m.ruin.blowPct;
      worstRuinAccount = m.a.name;
    }
  }

  const anyLocked = models.some((m) => m.locked || m.lifecycle === 'blown');
  const warnCount = atRisk.filter(isWarning).length;
  const health: HouseHealth = anyLocked ? 'danger' : warnCount ? 'warn' : 'ok';
  const healthLabel = anyLocked ? 'Locked / blown'
    : warnCount ? `${warnCount} at risk` : 'All clear';

  return {
    fundedBalance,
    todayPnl,
    weekPnl,
    ddBuffer,
    worstRuin,
    worstRuinAccount,
    health,
    healthLabel,
    fundedCount: fundedModels.length,
  };
}
